import { useEffect, useRef, type ReactNode } from 'react';

/* ---------------------------------------------------------------- icons -- */
/* Stroke icons at a single weight so the set reads as one family. */

type IconProps = { size?: number; className?: string };

const svg = (path: ReactNode, { size = 20, className }: IconProps, viewBox = '0 0 24 24') => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {path}
  </svg>
);

export const IconShield = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3 5 6v5.5c0 4.2 2.9 8.1 7 9.5 4.1-1.4 7-5.3 7-9.5V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </>,
    p,
  );

export const IconCamera = (p: IconProps) =>
  svg(
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </>,
    p,
  );

export const IconCheck = (p: IconProps) => svg(<path d="m5 13 4 4L19 7" />, p);

export const IconCheckCircle = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>,
    p,
  );

export const IconArrowLeft = (p: IconProps) =>
  svg(
    <>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </>,
    p,
  );

export const IconArrowRight = (p: IconProps) =>
  svg(
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>,
    p,
  );

export const IconDoor = (p: IconProps) =>
  svg(
    <>
      <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" />
      <path d="M4 21h16" />
      <circle cx="14.5" cy="12" r="1" fill="currentColor" stroke="none" />
    </>,
    p,
  );

export const IconSparkle = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.5 13.6 9 19 10.5 13.6 12 12 17.5 10.4 12 5 10.5 10.4 9 12 3.5Z" />
      <path d="M18.5 16.5 19 18.5l2 .5-2 .5-.5 2-.5-2-2-.5 2-.5.5-2Z" />
    </>,
    p,
  );

export const IconBath = (p: IconProps) =>
  svg(
    <>
      <path d="M4 12h16v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-2Z" />
      <path d="M7 12V6a2 2 0 0 1 4 0" />
      <path d="M6 19l-1 2M18 19l1 2" />
    </>,
    p,
  );

export const IconKitchen = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M4 10h16" />
      <path d="M8 6.5h.01M8 14h.01" />
    </>,
    p,
  );

export const IconBed = (p: IconProps) =>
  svg(
    <>
      <path d="M3 18V7" />
      <path d="M3 12h18v6" />
      <path d="M21 18v-4a2 2 0 0 0-2-2" />
      <circle cx="7.5" cy="9.5" r="1.75" />
    </>,
    p,
  );

export const IconClock = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 1.75" />
    </>,
    p,
  );

export const IconAlert = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16h.01" />
    </>,
    p,
  );

export const IconGlobe = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.5 9h17M3.5 15h17" />
      <path d="M12 3a14 14 0 0 0 0 18a14 14 0 0 0 0-18Z" />
    </>,
    p,
  );

export const IconLogout = (p: IconProps) =>
  svg(
    <>
      <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
      <path d="M10 8 6 12l4 4" />
      <path d="M6 12h9" />
    </>,
    p,
  );

export const IconEye = (p: IconProps) =>
  svg(
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>,
    p,
  );

export const IconEyeOff = (p: IconProps) =>
  svg(
    <>
      <path d="M4 4l16 16" />
      <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.3 4" />
      <path d="M6.3 7.9A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.3 0 2.4-.3 3.5-.7" />
      <path d="M9.9 10.2a3 3 0 0 0 4 4.2" />
    </>,
    p,
  );

export const IconRefresh = (p: IconProps) =>
  svg(
    <>
      <path d="M20 11A8 8 0 0 0 6.3 6.3L4 8.5" />
      <path d="M4 4v4.5h4.5" />
      <path d="M4 13a8 8 0 0 0 13.7 4.7L20 15.5" />
      <path d="M20 20v-4.5h-4.5" />
    </>,
    p,
  );

export const IconPlus = (p: IconProps) => svg(<path d="M12 5v14M5 12h14" />, p);

export const IconX = (p: IconProps) => svg(<path d="M6 6l12 12M18 6 6 18" />, p);

/* --------------------------------------------------------------- button -- */

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  className = '',
  disabled,
  ...rest
}: {
  children: ReactNode;
  variant?: 'primary' | 'accent' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size === 'lg' ? 'btn-lg' : size === 'sm' ? 'btn-sm' : '',
    block ? 'btn-block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" /> : icon}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- sheet -- */

/**
 * One component, two presentations: a bottom sheet on a phone, a centred dialog
 * from md up. Handles focus trapping, Escape, and Android/hardware Back.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
  footer,
  dismissible = true,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, dismissible]);

  // Back should close the sheet rather than leave the screen. The pushed entry is
  // tagged so a rewind can never undo a real navigation the app made meanwhile.
  useEffect(() => {
    if (!open || !dismissible) return;
    const token = `sheet-${Date.now()}`;
    history.pushState({ roomVerifySheet: token }, '');

    const onPopState = () => closeRef.current();
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      if ((history.state as { roomVerifySheet?: string } | null)?.roomVerifySheet === token) {
        history.back();
      }
    };
  }, [open, dismissible]);

  if (!open) return null;

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="sheet-grab" />
        <div className="sheet-head">
          <h2 id="sheet-title" className="sheet-title">
            {title}
          </h2>
          {dismissible && (
            <button className="icon-btn" onClick={onClose} aria-label="Close">
              <IconX size={18} />
            </button>
          )}
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- toasts -- */

export interface ToastMessage {
  id: number;
  text: string;
  tone: 'default' | 'success' | 'danger';
}

export function ToastRegion({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div className="toast-region" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.tone}`}>
          {toast.tone === 'success' && <IconCheckCircle size={16} />}
          {toast.tone === 'danger' && <IconAlert size={16} />}
          <span>{toast.text}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- progress ring -- */

export function ProgressRing({
  value,
  total,
  size = 56,
}: {
  value: number;
  total: number;
  size?: number;
}) {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = total > 0 ? Math.min(1, value / total) : 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--ink-200)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={ratio === 1 ? 'var(--success-fg)' : 'var(--accent-600)'}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - ratio)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 380ms cubic-bezier(.4,0,.2,1)' }}
      />
    </svg>
  );
}
